import type {Editor, Component} from 'grapesjs';
import {EMAIL_MAKER_PROPS} from '@EmailMakerReact/email-maker-react.constants';
import {openImageCropper} from '@EmailMakerReact/lib/email-maker-media-library';
import {removeComponentImageBackground} from '@EmailMakerReact/lib/email-maker-background-removal-library';
import {
  getAiImagesPanelKeyForComponent,
  openMyUploadsImage,
  openExploreMediaForSelection,
  openAiImages,
  openAiVideos,
  openMyUploadsVideo,
  openExploreMediaVideo,
  openEmbedVideo,
} from '@EmailMakerReact/lib/email-maker-media-modals-library';
import {openMessageModal} from '@Components/modals/message-modal';
import {openDesignSelectorAndInsert, openPostermakerForDesign} from '@EmailMakerReact/lib/email-maker-design-library';
import type {ModListDescriptor, ModListItem} from './mod-list-control.types';
import {MOD_LIST_ACTION_AI_IMAGES, MOD_LIST_ACTION_REMOVE_BACKGROUND} from './mod-list-control.types';

/** Icon + label key for every media action promoted to the mobile strip — shared with getPromotedMediaActions to prevent drift. */
export enum PromotedMediaAction {
  CROP = 'crop',
  REPLACE = 'replace',
  REMOVE_BACKGROUND = 'remove-background',
  AI_IMAGES = 'ai-images',
  AI_STICKERS = 'ai-stickers',
  EDIT_DESIGN = 'edit-design',
}

export interface PromotedMediaActionMeta {
  icon: string;
  labelKey: string;
  /** Shorter label used only on the mobile action strip where space is constrained. Falls back to `labelKey` when absent. */
  mobileLabelKey?: string;
}

export const PROMOTED_MEDIA_ACTION_META: Readonly<Record<PromotedMediaAction, PromotedMediaActionMeta>> = {
  [PromotedMediaAction.CROP]: {icon: 'icon-crop', labelKey: 'pmwjs_crop'},
  [PromotedMediaAction.REPLACE]: {icon: 'icon-replace', labelKey: 'pmwjs_replace'},
  [PromotedMediaAction.REMOVE_BACKGROUND]: {icon: 'icon-remove-bg', labelKey: 'pmwjs_ai_background_remover', mobileLabelKey: 'pmwjs_ai_bg_remover'},
  [PromotedMediaAction.AI_IMAGES]: {icon: 'icon-ai-image', labelKey: 'pmwjs_ai_images'},
  [PromotedMediaAction.AI_STICKERS]: {icon: 'icon-ai-sticker', labelKey: 'pmwjs_ai_stickers_caps'},
  [PromotedMediaAction.EDIT_DESIGN]: {icon: 'icon-pencil', labelKey: 'pmwjs_edit_design'},
} as const;

/** Sticker components surface the AI action as "AI Stickers"; the click routes through openAiImages either way (it presets the generator per component). */
export function getAiImagesActionForComponent(component: Component): PromotedMediaAction.AI_IMAGES | PromotedMediaAction.AI_STICKERS {
  return getAiImagesPanelKeyForComponent(component) === 'ai-stickers' ? PromotedMediaAction.AI_STICKERS : PromotedMediaAction.AI_IMAGES;
}

/** No-op used when ModListItem has subOptions — click expands sub-options rather than invoking the callback. */
const noOpSubOptionsCallback = (): void => {
  // intentional no-op
};

/** Shared "Replace" item (MyUploads / ExploreMedia / AIImages / MyDesigns) for image and design lists. */
function getImageAndDesignReplaceItem(editor: Editor, component: Component): ModListItem {
  const aiImagesMeta = PROMOTED_MEDIA_ACTION_META[getAiImagesActionForComponent(component)];
  return {
    iconClass: PROMOTED_MEDIA_ACTION_META[PromotedMediaAction.REPLACE].icon,
    label: window.i18next.t(PROMOTED_MEDIA_ACTION_META[PromotedMediaAction.REPLACE].labelKey),
    subOptions: [
      {
        label: window.i18next.t('pmwjs_myuploads'),
        icon: 'icon-upload',
        cb: (): void => {
          openMyUploadsImage(editor);
        },
      },
      {
        label: window.i18next.t('pmwjs_explore_media'),
        icon: 'icon-media',
        cb: (): void => {
          openExploreMediaForSelection(editor);
        },
      },
      {
        label: window.i18next.t(aiImagesMeta.labelKey),
        icon: aiImagesMeta.icon,
        cb: (): void => {
          openAiImages(editor);
        },
      },
      {
        label: window.i18next.t('pmwjs_my_designs'),
        icon: 'icon-designs',
        cb: (): void => {
          openDesignSelectorAndInsert(editor, {isMultiSelectAllowed: false, replaceComponent: true});
        },
      },
    ],
    callback: noOpSubOptionsCallback,
  };
}

/** Background removal for the component, resolving its element first. Shared by the desktop mod-list and the mobile action strip. */
export async function removeBackgroundForComponent(editor: Editor, component: Component): Promise<void> {
  const el = component.getEl();
  await removeComponentImageBackground(editor, component, el ?? undefined);
}

/** Opens the embedded poster editor for the design component, guarding the empty-design case. Shared by the desktop mod-list and the mobile action strip. */
export function editDesignComponent(editor: Editor, component: Component): void {
  const id = component.get(EMAIL_MAKER_PROPS.DESIGN_ID) as string | undefined;

  if (!id) {
    openMessageModal({
      title: window.i18next.t('pmwjs_no_design_added'),
      text: window.i18next.t('pmwjs_select_a_design_error'),
    });
    return;
  }

  openPostermakerForDesign(editor, {id});
}

/** Image components: crop, replace (with sub-options), background removal, AI images (AI stickers for sticker components). */
export function makeMediaModListDescriptor(editor: Editor, component: Component): ModListDescriptor {
  const aiImagesMeta = PROMOTED_MEDIA_ACTION_META[getAiImagesActionForComponent(component)];
  return {
    listClass: 'media-modification',
    items: [
      {
        property: EMAIL_MAKER_PROPS.CROP_DATA,
        iconClass: PROMOTED_MEDIA_ACTION_META[PromotedMediaAction.CROP].icon,
        label: window.i18next.t(PROMOTED_MEDIA_ACTION_META[PromotedMediaAction.CROP].labelKey),
        callback: (_editor: Editor, targetComponent: Component): void => {
          openImageCropper(_editor, targetComponent);
        },
      },
      getImageAndDesignReplaceItem(editor, component),
      {
        iconClass: `${PROMOTED_MEDIA_ACTION_META[PromotedMediaAction.REMOVE_BACKGROUND].icon} btn-link`,
        label: window.i18next.t(PROMOTED_MEDIA_ACTION_META[PromotedMediaAction.REMOVE_BACKGROUND].labelKey),
        action: MOD_LIST_ACTION_REMOVE_BACKGROUND,
        callback: removeBackgroundForComponent,
      },
      {
        iconClass: aiImagesMeta.icon,
        label: window.i18next.t(aiImagesMeta.labelKey),
        action: MOD_LIST_ACTION_AI_IMAGES,
        callback: (_editor: Editor): void => {
          openAiImages(_editor);
        },
      },
    ],
  };
}

/** Video components: replace with video-specific sub-options (MyUploads / ExploreMedia / Embed). */
export function makeVideoModListDescriptor(editor: Editor): ModListDescriptor {
  return {
    listClass: 'media-modification',
    items: [
      {
        iconClass: PROMOTED_MEDIA_ACTION_META[PromotedMediaAction.REPLACE].icon,
        label: window.i18next.t(PROMOTED_MEDIA_ACTION_META[PromotedMediaAction.REPLACE].labelKey),
        subOptions: [
          {
            label: window.i18next.t('pmwjs_myuploads'),
            icon: 'icon-upload',
            cb: (): void => {
              openMyUploadsVideo(editor);
            },
          },
          {
            label: window.i18next.t('pmwjs_explore_media'),
            icon: 'icon-media',
            cb: (): void => {
              openExploreMediaVideo(editor);
            },
          },
          {
            label: window.i18next.t('pmwjs_ai_videos'),
            icon: 'icon-video-ai',
            cb: (): void => {
              openAiVideos(editor);
            },
          },
          {
            label: window.i18next.t('pmwjs_embed_videos'),
            icon: 'icon-html',
            cb: (): void => {
              openEmbedVideo(editor);
            },
          },
        ],
        callback: noOpSubOptionsCallback,
      },
    ],
  };
}

/** Design components: edit design (opens postermaker), replace (image+design sub-options). */
export function makeDesignModListDescriptor(editor: Editor, component: Component): ModListDescriptor {
  return {
    listClass: 'design-modification',
    items: [
      {
        iconClass: PROMOTED_MEDIA_ACTION_META[PromotedMediaAction.EDIT_DESIGN].icon,
        label: window.i18next.t(PROMOTED_MEDIA_ACTION_META[PromotedMediaAction.EDIT_DESIGN].labelKey),
        callback: editDesignComponent,
      },
      getImageAndDesignReplaceItem(editor, component),
    ],
  };
}
